import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { Button } from "./ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Search, Users, Mail, PieChart, LogOut, ChevronRight, Edit2, Trash2, Send, Download, Filter, Menu, X, ChevronUp, ChevronDown } from "lucide-react";

// ezek a konstansok kellenek az apihoz, remélem nem változik a port
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState("registrations");
  const [registrations, setRegistrations] = useState([]);
  const [emails, setEmails] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [viewDetails, setViewDetails] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkInvStatus, setBulkInvStatus] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const STATUS_OPTIONS = ["fizetésre vár", "fizetett", "visszamondott", "inaktív"];
  const INVITATION_STATUS_OPTIONS = ["–", "leadva", "elkészítve"];

  // adat tültés fuggveny
  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    // Saját adatok lekérése
    try {
      const meResp = await axios.get(`${API}/users/me`, { headers });
      setCurrentUser(meResp.data);
    } catch (err) {
      console.error("Nem sikerült lekérni a saját adatokat");
    }

    // Egyesevel probaljuk meg, hogy ha egy hibás akkor a többi látszódjon
    const fetchFunc = async (url, setter) => {
      try {
        const resp = await axios.get(url, { headers });
        setter(resp.data);
      } catch (e) {
        console.error(`Hiba a lekérésnél: ${url}`, e);
        if (e.response && e.response.status === 401) onLogout();
      }
    };

    await Promise.all([
      fetchFunc(`${API}/tickets/registrations`, setRegistrations),
      fetchFunc(`${API}/admin/emails`, setEmails),
      fetchFunc(`${API}/admin/users`, setUsers),
      fetchFunc(`${API}/admin/stats`, setStats)
    ]);
    
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleResendEmail = async (logId) => {
     const token = localStorage.getItem("token");
     try {
       await axios.post(`${API}/admin/emails/${logId}/resend`, {}, {
         headers: { Authorization: `Bearer ${token}` }
       });
       alert("Email ujraküldve!");
       fetchData();
     } catch (e) {
       alert("Nem sikerült az ujraküldés");
     }
  };

  const handleDeleteRegistration = async (id) => {
    if (!window.confirm("Biztos törlöd? Végleges müvelet!")) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/tickets/registrations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (e) {
      alert("Hiba a törlésnél");
    }
  };

  const handleUpdateRegistration = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      await axios.put(`${API}/tickets/registrations/${selectedItem.id}`, selectedItem, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsEditing(false);
      setSelectedItem(null);
      fetchData();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Hiba a mentésnél";
      alert(errorMsg);
    }
  };

  const handleBulkUpdate = async (status, invStatus) => {
    if (selectedIds.length === 0) return;
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${API}/tickets/registrations/bulk-update`, {
        ids: selectedIds,
        status: status || undefined,
        invitationStatus: invStatus || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedIds([]);
      fetchData();
      alert(`Sikeresen frissítve ${selectedIds.length} regisztráció!`);
    } catch (e) {
      alert("Hiba a tömeges frissítésnél");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === registrations.length && registrations.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(registrations.map(r => r.id));
    }
  };

  const exportToExcel = (data, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendéglista");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportSelectedToExcel = () => {
    const selectedRegs = registrations.filter(r => selectedIds.includes(r.id));
    const exportData = [];
    selectedRegs.forEach(reg => {
      reg.participants.forEach(p => {
        exportData.push({
          "Reg. Kód": reg.id,
          "Kapcsolattartó": reg.name,
          "E-mail": reg.email,
          "Telefon": reg.phone,
          "Vendég Vezetéknév": p.lastName,
          "Vendég Keresztnév": p.firstName,
          "Vendég Előtag": p.prefix || "",
          "Választott Menü": p.menu,
          "Megjegyzés": p.note || "",
          "Sektor": p.field,
          "Típus": p.guestType || (p.isFreeGuest ? "Díszvendég" : "Teljes árú"),
          "Számlázási Név": reg.billing.name,
          "Számlázási Cím": reg.billing.address,
          "Adószám": reg.billing.taxId || "",
          "Honnan hallott": reg.referralSource || "",
          "Regisztráció Státusz": reg.status,
          "Meghívó Státusz": reg.invitationStatus || "–"
        });
      });
    });
    exportToExcel(exportData, `JB_kijelolt_regisztraciok_${new Date().toISOString().split('T')[0]}`);
  };

  const exportAllToExcel = () => {
    const exportData = [];
    registrations.forEach(reg => {
      reg.participants.forEach(p => {
        exportData.push({
          "Reg. Kód": reg.id,
          "Kapcsolattartó": reg.name,
          "Vendég": `${p.prefix || ""} ${p.lastName} ${p.firstName}`.trim(),
          "Menü": p.menu,
          "Sektor": p.field,
          "Típus": p.guestType || (p.isFreeGuest ? "Díszvendég" : "Teljes árú"),
          "Státusz": reg.status,
          "Meghívó": reg.invitationStatus || "–",
          "Honnan hallott": reg.referralSource || "",
          "E-mail": reg.email,
          "Számlázási Név": reg.billing.name
        });
      });
    });
    exportToExcel(exportData, `JB_teljes_vendeglista_${new Date().toISOString().split('T')[0]}`);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      await axios.put(`${API}/admin/users/${selectedItem.username}`, selectedItem, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsEditing(false);
      setSelectedItem(null);
      fetchData();
    } catch (e) {
      alert("Hiba a mentésnél");
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Biztos törlöd ${username} felhasználót?`)) return;
    const token = localStorage.getItem("token");
    try {
       await axios.delete(`${API}/admin/users/${username}`, {
         headers: { Authorization: `Bearer ${token}` }
       });
       fetchData();
    } catch (e) {
       alert("Hiba a törlésnél");
    }
  };

  // szürés és keresés a vendéglistához
  const filteredGuests = useMemo(() => {
    let guests = [];
    registrations.forEach(reg => {
      reg.participants.forEach(p => {
        guests.push({
          ...p,
          regId: reg.id,
          contactName: reg.name,
          contactEmail: reg.email,
          billingName: reg.billing.name,
          fullName: `${p.prefix} ${p.lastName} ${p.firstName}`.trim()
        });
      });
    });

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      guests = guests.filter(g => 
        g.fullName.toLowerCase().includes(s) || 
        g.contactName.toLowerCase().includes(s) ||
        g.billingName.toLowerCase().includes(s)
      );
    }

    return guests.sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [registrations, searchTerm]);

  // ez a menü oldal, remélem átlátható
  return (
    <div className="flex h-screen bg-[#050505] text-white font-sans">
      {/* SIDEBAR - ide került minden fontos gomb */}
      <div className={`fixed inset-y-0 left-0 z-50 transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:relative md:translate-x-0 transition-transform duration-300 ease-in-out ${sidebarOpen ? "w-64" : "w-0 md:w-20"} border-r border-white/10 flex flex-col p-6 bg-[#0a0a0a] overflow-hidden`}>
        <div className="flex justify-between items-center mb-12">
          <h1 className={`text-xl font-bold text-[#d4af37] tracking-[0.2em] uppercase ${!sidebarOpen && "hidden"}`}>JB Admin</h1>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-500 hover:text-white md:hidden">
            <X size={20} />
          </button>
        </div>
        
        <nav className="flex-1 space-y-2">
          <button 
            onClick={() => setActiveTab("registrations")}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded text-sm transition-all ${activeTab === "registrations" ? "bg-[#d4af37] text-black font-bold" : "hover:bg-white/5 text-gray-400"}`}
          >
            <Users size={18} /> <span className={!sidebarOpen ? "md:hidden" : ""}>{currentUser?.is_admin ? "Regisztrációk" : "Saját Jegyek"}</span>
          </button>
          
          {currentUser?.is_admin && (
            <>
              <button 
                onClick={() => setActiveTab("emails")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded text-sm transition-all ${activeTab === "emails" ? "bg-[#d4af37] text-black font-bold" : "hover:bg-white/5 text-gray-400"}`}
              >
                <Mail size={18} /> <span className={!sidebarOpen ? "md:hidden" : ""}>E-mail Napló</span>
              </button>
              <button 
                onClick={() => setActiveTab("users")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded text-sm transition-all ${activeTab === "users" ? "bg-[#d4af37] text-black font-bold" : "hover:bg-white/5 text-gray-400"}`}
              >
                <Users size={18} /> <span className={!sidebarOpen ? "md:hidden" : ""}>Felhasználók</span>
              </button>
              <button 
                onClick={() => setActiveTab("stats")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded text-sm transition-all ${activeTab === "stats" ? "bg-[#d4af37] text-black font-bold" : "hover:bg-white/5 text-gray-400"}`}
              >
                <PieChart size={18} /> <span className={!sidebarOpen ? "md:hidden" : ""}>Statisztika</span>
              </button>
            </>
          )}
        </nav>

        <button 
          onClick={onLogout}
          className="mt-auto flex items-center gap-3 px-4 py-3 rounded text-sm text-red-500 hover:bg-red-500/10 transition-all border border-red-500/20"
        >
          <LogOut size={18} /> <span className={!sidebarOpen ? "md:hidden" : ""}>Kijelentkezés</span>
        </button>
      </div>

      {/* MAIN CONTENT - itt van az igazi meló */}
      <div className="flex-1 overflow-y-auto p-6 md:p-12 relative">
        {/* Toggle button for collapsed view */}
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={`fixed md:absolute top-6 left-6 z-40 p-2 bg-[#d4af37] text-black rounded shadow-lg transition-all ${sidebarOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          <Menu size={20} />
        </button>
        {loading ? (
          <div className="flex justify-center items-center h-full">betöltésalatt...</div>
        ) : (
          <>
            {/* REGISTRATIONS TAB */}
            {activeTab === "registrations" && (
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-8">
                  <div className={sidebarOpen ? "md:pl-0" : "md:pl-12"}>
                    <h2 className="text-2xl md:text-3xl font-bold mb-2 uppercase tracking-tighter">Regisztrációk</h2>
                    <p className="text-gray-500 text-sm">Összesen {registrations.length} jegyrendelés van rögzitve.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                    {currentUser?.is_admin && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="bg-white/5 border-white/10 text-[#d4af37] gap-2 text-[10px] md:text-xs"
                        onClick={exportAllToExcel}
                      >
                        <Download size={14} /> Excel (Összes)
                      </Button>
                    )}
                    <div className="flex items-center gap-2">
                       <input 
                         type="checkbox" 
                         checked={selectedIds.length === registrations.length && registrations.length > 0} 
                         onChange={toggleSelectAll}
                         className="accent-[#d4af37] w-4 h-4 cursor-pointer"
                       />
                       <span className="text-[10px] text-gray-500 uppercase font-bold">Mind</span>
                    </div>
                    <div className="relative flex-1 md:w-72">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                      <Input 
                        placeholder="Keresés..." 
                        className="pl-10 bg-[#111] border-white/10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {registrations.filter(r => 
                    r.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                    r.id.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map(reg => (
                    <div key={reg.id} className={`bg-[#111] border p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:border-[#d4af37]/30 transition-all group ${selectedIds.includes(reg.id) ? 'border-[#d4af37]' : 'border-white/5'}`}>
                      <div className="flex items-start md:items-center gap-4 md:gap-6">
                        <input 
                          type="checkbox" 
                          checked={selectedIds.includes(reg.id)} 
                          onChange={() => {
                            if (selectedIds.includes(reg.id)) {
                              setSelectedIds(selectedIds.filter(id => id !== reg.id));
                            } else {
                              setSelectedIds([...selectedIds, reg.id]);
                            }
                          }}
                          className="accent-[#d4af37] w-5 h-5 cursor-pointer"
                        />
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-[10px] bg-[#d4af37]/10 text-[#d4af37] px-2 py-0.5 font-bold font-mono tracking-widest">{reg.id}</span>
                            <span className={`text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-widest ${
                                 reg.status === 'fizetett' ? 'bg-green-500/20 text-green-500' :
                                 reg.status === 'visszamondott' ? 'bg-red-500/20 text-red-500' :
                                 reg.status === 'inaktív' ? 'bg-gray-500/20 text-gray-500' :
                                 'bg-yellow-500/20 text-yellow-500'
                               }`}>
                                  {reg.status || 'fizetésre vár'}
                            </span>
                            <h3 className="font-bold text-lg text-white">{reg.name}</h3>
                          </div>
                          <div className="text-xs md:text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                            <span>{reg.email}</span>
                            <span className="text-gray-800 hidden md:inline">|</span>
                            <span>{reg.ticket_type}</span>
                            <span className="text-gray-800 hidden md:inline">|</span>
                            <span>{reg.participants.length} fő</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 w-full md:w-auto mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-white/5">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-white/5 text-gray-400 gap-2 px-3 h-9"
                          onClick={() => { setSelectedItem(reg); setIsEditing(false); setViewDetails(true); }}
                        >
                          <ChevronRight size={16} /> Részletek
                        </Button>
                        
                        { (currentUser?.is_admin || reg.owner_username === currentUser?.username || reg.email === currentUser?.email) && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className={`h-9 w-9 ${ (reg.invitationStatus === 'leadva' || reg.invitationStatus === 'elkészítve') && !currentUser?.is_admin ? 'text-gray-700 cursor-not-allowed' : 'text-[#d4af37]' }`}
                            onClick={() => { 
                              if ((reg.invitationStatus === 'leadva' || reg.invitationStatus === 'elkészítve') && !currentUser?.is_admin) {
                                alert("A regisztráció már nem módosítható, mert a meghívók le lettek adva megírásra.");
                                return;
                              }
                              setSelectedItem(reg); 
                              setIsEditing(true); 
                              setViewDetails(false); 
                            }}
                          >
                            <Edit2 size={16} />
                          </Button>
                        )}
                        
                        {currentUser?.is_admin && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="hover:bg-red-500/20 text-red-500 h-9 w-9"
                            onClick={() => handleDeleteRegistration(reg.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* EMAILS TAB */}
            {activeTab === "emails" && (
              <div className="space-y-6">
                <h2 className="text-3xl font-bold mb-8 uppercase tracking-tighter">E-mail Napló</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b border-white/10 text-gray-500 uppercase text-[10px] tracking-widest">
                      <tr>
                        <th className="pb-4 font-normal">Időpont</th>
                        <th className="pb-4 font-normal">Címzett</th>
                        <th className="pb-4 font-normal">Tárgy</th>
                        <th className="pb-4 font-normal">Státusz</th>
                        <th className="pb-4 font-normal text-right">Müveletek</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {emails.map(log => (
                        <tr key={log.id} className="group hover:bg-white/[0.02]">
                          <td className="py-4 text-gray-500">{new Date(log.timestamp).toLocaleString()}</td>
                          <td className="py-4 font-medium text-white">{log.recipient}</td>
                          <td className="py-4 text-gray-400">{log.subject}</td>
                          <td className="py-4">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${log.status === "sent" ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"}`}>
                              {log.status === "sent" ? "ELKÜLDVE" : "HIBA"}
                            </span>
                          </td>
                          <td className="py-4 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-[#d4af37] hover:bg-[#d4af37]/10"
                              onClick={() => handleResendEmail(log.id)}
                            >
                              <Send size={14} className="mr-2" /> Újraküldés
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* USERS TAB */}
            {activeTab === "users" && (
              <div className="space-y-6">
                <h2 className="text-3xl font-bold mb-8 uppercase tracking-tighter">Felhasználók</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {users.map(u => (
                    <Card key={u.username} className="bg-[#111] border-white/5">
                      <CardHeader>
                        <CardTitle className="flex justify-between items-center text-white">
                          <span>{u.username}</span>
                          {u.is_admin && <span className="text-[10px] bg-red-500/10 text-red-500 px-2 py-0.5 rounded">ADMIN</span>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-500 mb-4">{u.email || "Nincs email rögzitve"}</p>
                        <div className="flex gap-2">
                             <Button 
                               variant="outline" 
                               size="sm" 
                               className="w-full border-white/10 hover:bg-white/5 text-white"
                               onClick={() => { setSelectedItem(u); setIsEditing(true); }}
                             >
                               Szerkesztés
                             </Button>
                           <Button 
                             variant="ghost" 
                             size="sm" 
                             className="text-red-500 hover:bg-red-500/10"
                             onClick={() => handleDeleteUser(u.username)}
                           >
                             <Trash2 size={16} />
                           </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* STATS TAB - itt kértél sokat, remélem elégedett leszel */}
            {activeTab === "stats" && stats && (
              <div className="space-y-12">
                <h2 className="text-3xl font-bold mb-8 uppercase tracking-tighter">Statisztika és Vendéglista</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <Card className="bg-[#111] border-white/5 p-6 flex flex-col justify-between">
                    <p className="text-xs uppercase tracking-widest text-[#d4af37] mb-2 font-bold">Össz Bevétel</p>
                    <h3 className="text-3xl font-black text-white">{stats.total_revenue.toLocaleString()} Ft</h3>
                  </Card>
                  <Card className="bg-[#111] border-white/5 p-6 flex flex-col justify-between">
                    <p className="text-xs uppercase tracking-widest text-white/70 mb-2 font-bold">Összes Vendég</p>
                    <h3 className="text-3xl font-black text-white">{stats.total_guests} fő</h3>
                  </Card>
                  <Card className="bg-[#111] border-white/5 p-6 flex flex-col justify-between">
                    <p className="text-xs uppercase tracking-widest text-white/70 mb-2 font-bold">VIP (Díszvendég)</p>
                    <h3 className="text-3xl font-black text-[#d4af37]">{stats.vip_count} fő</h3>
                  </Card>
                  <Card className="bg-[#111] border-white/5 p-6 flex flex-col justify-between">
                    <p className="text-xs uppercase tracking-widest text-white/70 mb-2 font-bold">Regisztrációk</p>
                    <h3 className="text-3xl font-black text-white">{stats.total_registrations} db</h3>
                  </Card>
                </div>

                <div className="bg-[#111] border border-white/5 p-8">
                  <div className="flex justify-between items-center mb-8 border-b border-white/5 pb-4">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      <Download size={20} className="text-[#d4af37]" /> Teljes Vendéglista (ABC sorrend)
                    </h3>
                    <div className="relative w-64">
                      <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                      <Input 
                        placeholder="Szűrés bárhová..." 
                        className="pl-10 h-9 bg-black border-white/10 text-xs"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-gray-500 uppercase tracking-widest bg-white/[0.02]">
                        <tr>
                          <th className="py-3 px-4">Vendég Neve</th>
                          <th className="py-3 px-4">Szektor</th>
                          <th className="py-3 px-4">Kapcsolattartó</th>
                          <th className="py-3 px-4">Számlázási Név</th>
                          <th className="py-3 px-4">Státusz</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {filteredGuests.map((g, idx) => (
                          <tr key={`${g.regId}-${idx}`} className="hover:bg-white/[0.02] transition-colors">
                            <td className="py-4 px-4 font-bold text-white">{g.fullName}</td>
                            <td className="py-4 px-4 text-gray-400">{g.field}</td>
                            <td className="py-4 px-4 text-white font-medium">{g.contactName}</td>
                            <td className="py-4 px-4">
                               {g.guestType === "Díszvendég" ? (
                                 <span className="text-[9px] bg-[#d4af37] text-black px-2 py-0.5 rounded font-black">DÍSZVENDÉG</span>
                               ) : g.guestType === "Partner" ? (
                                 <span className="text-[9px] bg-[#d4af37]/20 text-[#d4af37] px-2 py-0.5 rounded font-black">PARTNER</span>
                               ) : (
                                 <span className="text-[9px] bg-white/5 text-gray-500 px-2 py-0.5 rounded font-black">TELJES ÁRÚ</span>
                               )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {isEditing && selectedItem && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex justify-center items-center p-4">
          <div className="bg-[#111] border border-white/10 p-4 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
             <h2 className="text-2xl font-bold mb-6 flex justify-between uppercase">
                {selectedItem.username ? `User: ${selectedItem.username}` : `Reg: ${selectedItem.id}`}
                <button onClick={() => { setIsEditing(false); setSelectedItem(null); }} className="text-gray-500 hover:text-white">&times;</button>
             </h2>
             
             {selectedItem.username ? (
                <form onSubmit={handleUpdateUser} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="text-xs uppercase text-gray-500 mb-2 block">E-mail Cím</label>
                      <Input value={selectedItem.email || ""} onChange={e => setSelectedItem({...selectedItem, email: e.target.value})} className="bg-[#1a1a1a] border-white/5" />
                    </div>
                    <div className="flex items-center gap-3">
                      <input 
                        type="checkbox" 
                        id="isAdmin"
                        checked={selectedItem.is_admin} 
                        onChange={e => setSelectedItem({...selectedItem, is_admin: e.target.checked})} 
                        className="accent-[#d4af37]"
                      />
                      <label htmlFor="isAdmin" className="text-xs uppercase text-[#d4af37] font-bold">Admin Privilégiumok</label>
                    </div>
                  </div>
                  <div className="flex gap-4 pt-6 border-t border-white/5">
                     <Button type="submit" className="flex-1 bg-[#d4af37] text-black font-bold">Adatok Frissítése</Button>
                     <Button type="button" variant="outline" onClick={() => setIsEditing(false)} className="flex-1 border-white/10">Mégse</Button>
                  </div>
                </form>
             ) : (
                <form onSubmit={handleUpdateRegistration} className="space-y-6 text-left">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label className="text-xs uppercase text-gray-500 mb-2 block">Kapcsolattartó Neve</label>
                      <Input disabled={!currentUser?.is_admin} value={selectedItem.name} onChange={e => setSelectedItem({...selectedItem, name: e.target.value})} className="bg-[#1a1a1a] border-white/5 disabled:opacity-50" />
                    </div>
                    <div>
                      <label className="text-xs uppercase text-gray-500 mb-2 block">E-mail Cím</label>
                      <Input disabled={!currentUser?.is_admin} value={selectedItem.email} onChange={e => setSelectedItem({...selectedItem, email: e.target.value})} className="bg-[#1a1a1a] border-white/5 disabled:opacity-50" />
                    </div>
                    <div>
                      <label className="text-xs uppercase text-gray-500 mb-2 block">Telefonszám</label>
                      <Input disabled={!currentUser?.is_admin} value={selectedItem.phone} onChange={e => setSelectedItem({...selectedItem, phone: e.target.value})} className="bg-[#1a1a1a] border-white/5 disabled:opacity-50" />
                    </div>
                    <div>
                      <label className="text-xs uppercase text-gray-500 mb-2 block">Rendszám</label>
                      <Input value={selectedItem.licensePlate || ""} onChange={e => setSelectedItem({...selectedItem, licensePlate: e.target.value})} className="bg-[#1a1a1a] border-white/5" />
                    </div>
                    <div>
                      <label className="text-xs uppercase text-gray-500 mb-2 block">Honnan hallott a bálról?</label>
                      <Input value={selectedItem.referralSource || ""} onChange={e => setSelectedItem({...selectedItem, referralSource: e.target.value})} className="bg-[#1a1a1a] border-white/5" />
                    </div>
                    
                    {currentUser?.is_admin && (
                      <>
                        <div>
                          <label className="text-xs uppercase text-gray-500 mb-2 block">Regisztráció Státusza</label>
                          <select 
                            value={selectedItem.status || "fizetésre vár"} 
                            onChange={e => setSelectedItem({...selectedItem, status: e.target.value})}
                            className="w-full bg-[#1a1a1a] border border-white/5 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#d4af37]"
                          >
                            {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs uppercase text-gray-500 mb-2 block">Meghívó Státusza</label>
                          <select 
                            value={selectedItem.invitationStatus || "–"} 
                            onChange={e => setSelectedItem({...selectedItem, invitationStatus: e.target.value})}
                            className="w-full bg-[#1a1a1a] border border-white/5 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#d4af37]"
                          >
                            {INVITATION_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Résztvevők szerkesztése */}
                  <div className="pt-6 border-t border-white/5">
                    <h3 className="text-sm font-bold uppercase mb-4 text-[#d4af37]">Résztvevők és Menü</h3>
                    {!currentUser?.is_admin && (
                      <p className="text-[10px] text-gray-500 mb-4 uppercase tracking-widest leading-relaxed">
                        A résztvevők nevét és sorrendjét módosíthatja. A menü és szektor módosításához kérjük vegye fel a kapcsolatot a szervezőkkel.
                      </p>
                    )}
                    <div className="space-y-4">
                      {(selectedItem.participants || []).map((p, idx) => (
                        <div key={idx} className="bg-white/5 p-4 rounded-lg flex flex-col gap-3 relative">
                           <div className="flex justify-between items-center mb-1">
                              <span className="text-[10px] font-black text-[#d4af37]">{idx + 1}. RÉSZTVEVŐ</span>
                              <div className="flex items-center gap-2">
                                <button 
                                  type="button"
                                  disabled={idx === 0}
                                  onClick={() => {
                                    const newP = [...selectedItem.participants];
                                    [newP[idx], newP[idx-1]] = [newP[idx-1], newP[idx]];
                                    setSelectedItem({...selectedItem, participants: newP});
                                  }}
                                  className="text-gray-500 hover:text-white disabled:opacity-20"
                                >
                                  <ChevronUp size={14} />
                                </button>
                                <button 
                                  type="button"
                                  disabled={idx === selectedItem.participants.length - 1}
                                  onClick={() => {
                                    const newP = [...selectedItem.participants];
                                    [newP[idx], newP[idx+1]] = [newP[idx+1], newP[idx]];
                                    setSelectedItem({...selectedItem, participants: newP});
                                  }}
                                  className="text-gray-500 hover:text-white disabled:opacity-20"
                                >
                                  <ChevronDown size={14} />
                                </button>
                              </div>
                           </div>
                           <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                             <Input placeholder="Vezetéknév" value={p.lastName} onChange={e => {
                               const newP = [...selectedItem.participants];
                               newP[idx].lastName = e.target.value;
                               setSelectedItem({...selectedItem, participants: newP});
                             }} className="bg-[#111] border-white/5 text-[11px]" />
                             <Input placeholder="Keresztnév" value={p.firstName} onChange={e => {
                               const newP = [...selectedItem.participants];
                               newP[idx].firstName = e.target.value;
                               setSelectedItem({...selectedItem, participants: newP});
                             }} className="bg-[#111] border-white/5 text-[11px]" />
                             <Input placeholder="Prefix (pl. Dr.)" value={p.prefix} onChange={e => {
                               const newP = [...selectedItem.participants];
                               newP[idx].prefix = e.target.value;
                               setSelectedItem({...selectedItem, participants: newP});
                             }} className="bg-[#111] border-white/5 text-[11px]" />
                           </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[8px] uppercase text-gray-700 block mb-1">Választott Menü</label>
                                <Input disabled={!currentUser?.is_admin} placeholder="Menü" value={p.menu} onChange={e => {
                                  const newP = [...selectedItem.participants];
                                  newP[idx].menu = e.target.value;
                                  setSelectedItem({...selectedItem, participants: newP});
                                }} className="bg-[#111] border-white/5 text-[11px] disabled:opacity-50" />
                              </div>
                              
                              <div>
                                <label className="text-[8px] uppercase text-gray-700 block mb-1">Érkezési Szektor</label>
                                <select 
                                  value={p.field || "Jog"} 
                                  onChange={e => {
                                    const newP = [...selectedItem.participants];
                                    newP[idx].field = e.target.value;
                                    setSelectedItem({...selectedItem, participants: newP});
                                  }}
                                  className="w-full bg-[#111] border border-white/5 rounded px-2 py-2 text-[11px] text-white focus:outline-none focus:border-[#d4af37]"
                                >
                                  <option value="Jog">Jog</option>
                                  <option value="Gazdaság">Gazdaság</option>
                                  <option value="Egyéb">Egyéb</option>
                                </select>
                              </div>
                            </div>
                            
                            <div className="mt-2">
                              {currentUser?.is_admin ? (
                                <div className="flex flex-col gap-2">
                                  <label className="text-[8px] uppercase text-gray-700 block">Résztvevő típusa</label>
                                  <div className="flex gap-2">
                                    {["Teljes árú", "Díszvendég", "Partner"].map(t => (
                                      <button 
                                        key={t}
                                        type="button"
                                        onClick={() => {
                                          const newP = [...selectedItem.participants];
                                          newP[idx].guestType = t;
                                          newP[idx].isFreeGuest = (t === "Díszvendég"); // legacy
                                          setSelectedItem({...selectedItem, participants: newP});
                                        }}
                                        className={`text-[9px] px-2 py-1 rounded font-bold transition-all ${
                                          (p.guestType === t || (!p.guestType && t === "Teljes árú" && !p.isFreeGuest) || (!p.guestType && t === "Díszvendég" && p.isFreeGuest))
                                            ? 'bg-[#d4af37] text-black' 
                                            : 'bg-white/5 text-gray-500 hover:text-white'
                                        }`}
                                      >
                                        {t}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-[9px] font-bold text-[#d4af37] uppercase">
                                  {p.guestType || (p.isFreeGuest ? "Díszvendég" : "Teljes árú")}
                                </div>
                              )}
                            </div>
                           {currentUser?.is_admin && (
                            <button 
                              type="button" 
                              onClick={() => {
                                const newP = selectedItem.participants.filter((_, i) => i !== idx);
                                setSelectedItem({...selectedItem, participants: newP});
                              }} 
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow-lg"
                            >
                              &times;
                            </button>
                           )}
                        </div>
                      ))}
                      {currentUser?.is_admin && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="w-full border border-dashed border-white/10 text-gray-500 hover:text-white"
                          onClick={() => {
                            const newP = [...selectedItem.participants, { prefix: "", firstName: "", lastName: "", menu: "Négyfogásos vacsora Pallag Dávid séftől", field: "Jog", isFreeGuest: false }];
                            setSelectedItem({...selectedItem, participants: newP});
                          }}
                        >
                          + Új Résztvevő Hozzáadása
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Számlázási adatok */}
                  <div className="pt-6 border-t border-white/5">
                    <h3 className="text-sm font-bold uppercase mb-4 text-[#d4af37]">Számlázási Adatok</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <Input placeholder="Számlázási Név" value={selectedItem.billing?.name || ""} onChange={e => setSelectedItem({...selectedItem, billing: {...(selectedItem.billing || {}), name: e.target.value}})} className="bg-[#1a1a1a] border-white/5" />
                      <Input placeholder="Cím" value={selectedItem.billing?.address || ""} onChange={e => setSelectedItem({...selectedItem, billing: {...(selectedItem.billing || {}), address: e.target.value}})} className="bg-[#1a1a1a] border-white/5" />
                      <Input placeholder="Adószám (opcionális)" value={selectedItem.billing?.taxNumber || ""} onChange={e => setSelectedItem({...selectedItem, billing: {...(selectedItem.billing || {}), taxNumber: e.target.value}})} className="bg-[#1a1a1a] border-white/5" />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-8 border-t border-white/10">
                     <Button type="submit" className="flex-1 bg-[#d4af37] text-black font-bold uppercase tracking-widest text-xs py-6">Változtatások Mentése</Button>
                     <Button type="button" variant="outline" onClick={() => setIsEditing(false)} className="flex-1 border-white/10 py-6 text-xs uppercase tracking-widest">Mégse</Button>
                  </div>
                </form>
             )}
          </div>
        </div>
      )}

      {/* DETAILS MODAL */}
      {viewDetails && selectedItem && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex justify-center items-center p-4">
          <div className="bg-[#111] border border-white/10 p-4 md:p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
             <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-3xl font-black uppercase tracking-tighter mb-2">REGISZTRÁCIÓ RÉSZLETEI</h2>
                  <div className="flex items-center gap-4">
                    <span className="text-[#d4af37] font-mono font-bold tracking-[0.2em]">{selectedItem.id}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-widest ${
                         selectedItem.status === 'fizetett' ? 'bg-green-500/20 text-green-500' :
                         selectedItem.status === 'visszamondott' ? 'bg-red-500/20 text-red-500' :
                         selectedItem.status === 'inaktív' ? 'bg-gray-500/20 text-gray-500' :
                         'bg-yellow-500/20 text-yellow-500'
                       }`}>
                          {selectedItem.status || 'fizetésre vár'}
                    </span>
                    <span className="text-[10px] bg-blue-500/20 text-blue-500 px-2 py-0.5 rounded font-black uppercase tracking-widest">
                       Meghívó: {selectedItem.invitationStatus || '–'}
                    </span>
                    {selectedItem.paid_at && (
                      <span className="text-[10px] bg-[#d4af37]/20 text-[#d4af37] px-2 py-0.5 rounded font-black uppercase tracking-widest">
                         Befizetve: {new Date(selectedItem.paid_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => { setViewDetails(false); setSelectedItem(null); }} className="text-gray-500 hover:text-white text-3xl font-light">&times;</button>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase text-[#d4af37] border-b border-white/5 pb-2">KAPCSOLATTARTÓ</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] uppercase text-gray-600 block">Név</label>
                      <div className="text-white font-medium">{selectedItem.name}</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-gray-600 block">E-mail</label>
                      <div className="text-white font-medium">{selectedItem.email}</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-gray-600 block">Telefon</label>
                      <div className="text-white font-medium">{selectedItem.phone}</div>
                    </div>
                    {selectedItem.referralSource && (
                      <div>
                        <label className="text-[10px] uppercase text-gray-600 block">Honnan hallott</label>
                        <div className="text-white font-medium">{selectedItem.referralSource}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase text-[#d4af37] border-b border-white/5 pb-2">SZÁMLÁZÁSI ADATOK</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] uppercase text-gray-600 block">Név / Cég</label>
                      <div className="text-white font-medium">{selectedItem.billing?.name || "Nincs megadva"}</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-gray-600 block">Cím</label>
                      <div className="text-white font-medium">{selectedItem.billing?.address || "Nincs megadva"}</div>
                    </div>
                    {selectedItem.billing?.taxNumber && (
                      <div>
                        <label className="text-[10px] uppercase text-gray-600 block">Adószám</label>
                        <div className="text-white font-medium">{selectedItem.billing.taxNumber}</div>
                      </div>
                    )}
                  </div>
                </div>
             </div>

             <div className="space-y-6 mb-12">
                <h3 className="text-sm font-bold uppercase text-[#d4af37] border-b border-white/5 pb-2">RÉSZTVEVŐK ({(selectedItem.participants || []).length} fő)</h3>
                <div className="grid grid-cols-1 gap-4">
                  {(selectedItem.participants || []).map((p, idx) => (
                    <div key={idx} className="bg-white/[0.02] border border-white/5 p-4 flex justify-between items-center group hover:bg-white/[0.04]">
                      <div>
                        <div className="text-white font-bold text-sm">{p.prefix} {p.lastName} {p.firstName}</div>
                        <div className="text-[11px] text-gray-500 mt-1 uppercase tracking-wider">{p.menu}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[11px] text-[#d4af37] font-medium uppercase tracking-tighter">{p.field} szektor</div>
                        {p.isFreeGuest && <span className="text-[9px] bg-[#d4af37]/20 text-[#d4af37] px-2 py-0.5 rounded font-black mt-1 inline-block">VIP</span>}
                      </div>
                    </div>
                  ))}
                </div>
             </div>

             <div className="flex justify-end gap-4 pt-8 border-t border-white/10">
                <Button 
                   variant="outline" 
                   onClick={() => { setViewDetails(false); setIsEditing(true); setViewDetails(false); }} 
                   className="border-white/10 text-xs uppercase tracking-widest text-white"
                >
                   Szerkesztés
                </Button>
                <Button 
                   onClick={() => setViewDetails(false)} 
                   className="bg-white text-black font-bold uppercase tracking-widest text-xs px-8"
                >
                   Bezárás
                </Button>
             </div>
          </div>
        </div>
      )}

      {/* BULK ACTION BAR */}
      {selectedIds.length > 0 && currentUser?.is_admin && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-[#1a1a1a] border border-[#d4af37]/30 text-white px-8 py-6 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col md:flex-row items-center gap-8 z-[150] animate-in fade-in slide-in-from-bottom-4 duration-500">
           <div className="flex items-center gap-4 border-b md:border-b-0 md:border-r border-white/10 pb-4 md:pb-0 md:pr-8 w-full md:w-auto">
              <div className="bg-[#d4af37] text-black w-10 h-10 rounded-full flex items-center justify-center font-black text-xl">
                 {selectedIds.length}
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-black tracking-widest text-[#d4af37]">Kijelölve</span>
                <button onClick={() => setSelectedIds([])} className="text-[10px] text-gray-500 hover:text-white uppercase font-bold text-left">Összes törlése</button>
              </div>
           </div>
           
           <div className="flex flex-wrap items-center gap-6">
              <div className="flex flex-col min-w-[140px]">
                 <span className="text-[9px] uppercase font-black mb-1.5 text-gray-500">Regisztráció</span>
                 <select 
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    className="bg-black border border-white/10 text-xs font-bold focus:border-[#d4af37] cursor-pointer rounded-lg px-3 py-2 outline-none"
                 >
                    <option value="">Nincs változtatás</option>
                    {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                 </select>
              </div>

              <div className="flex flex-col min-w-[140px]">
                 <span className="text-[9px] uppercase font-black mb-1.5 text-gray-500">Meghívó</span>
                 <select 
                    value={bulkInvStatus}
                    onChange={(e) => setBulkInvStatus(e.target.value)}
                    className="bg-black border border-white/10 text-xs font-bold focus:border-[#d4af37] cursor-pointer rounded-lg px-3 py-2 outline-none"
                 >
                    <option value="">Nincs változtatás</option>
                    {INVITATION_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                 </select>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => handleBulkUpdate(bulkStatus, bulkInvStatus)}
                  disabled={!bulkStatus && !bulkInvStatus}
                  className="bg-[#d4af37] hover:bg-[#b8962d] text-black font-black uppercase text-[10px] tracking-widest px-6 h-10 rounded-lg"
                >
                  Változtatások Mentése
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={exportSelectedToExcel}
                  className="border-white/10 hover:bg-white/5 text-gray-300 font-bold uppercase text-[10px] tracking-widest px-4 h-10 rounded-lg flex gap-2"
                >
                  <Download size={14} /> Excel
                </Button>
              </div>
           </div>

           <button 
             onClick={() => setSelectedIds([])}
             className="hidden md:block text-gray-500 hover:text-white transition-opacity border-l border-white/10 pl-8"
           >
             <ChevronRight className="rotate-90 md:rotate-0" />
           </button>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
